import React, { FC, useState, useMemo, useEffect } from 'react';

import { LoyaltiesService, LoyaltyType } from '@api/index';
import TurkTelekomModal, {
  TurkTelekomModalFormData,
} from '@components/ui/modals/PromotionModal/TurkTelekom';
import HopiModal, {
  HopiModalFormData,
} from '@components/ui/modals/PromotionModal/HopiModal';
import KocAilem, {
  KocAilemModalFormData,
} from '@components/ui/modals/PromotionModal/KocAilem';
import Zubizu, {
  ZubizuModalFormData,
} from '@components/ui/modals/PromotionModal/Zubizu/Zubizu';
import ErrorModal from '@components/ui/modals/ErrorModal';
import { useBasketId, storeLoyalty } from '@hooks/cache';
import { callService } from '@api/caller';

const modals = {
  TurkTelekom: TurkTelekomModal,
  TurkTelekomPrime: TurkTelekomModal,
  TurkTelekomPrimeBusiness: TurkTelekomModal,
  KocAilem: KocAilem,
  HopiKazanim: HopiModal,
  Zubizu: Zubizu,
};

export type { LoyaltyType };

interface LoyaltyVerificationProps {
  action?: () => void;
  loyaltyType: LoyaltyType;
  onCancel?: () => void;
}

const getTurkTelekomService = (loyaltyType: LoyaltyType) => {
  switch (loyaltyType) {
    case LoyaltyType.TURK_TELEKOM:
      return 'validateTurkTelekom';
    case LoyaltyType.TURK_TELEKOM_PRIME:
      return 'validateTurkTelekomPrime';
    case LoyaltyType.TURK_TELEKOM_PRIME_BUSINESS:
      return 'validateTurkTelekomBusinessPrime';

    default:
      return 'validateTurkTelekom';
  }
};

const LoyaltyVerifications: FC<LoyaltyVerificationProps> = ({
  action,
  loyaltyType,
  onCancel,
}) => {
  const [error, setError] = useState(false);
  const [showModal, setShowModal] = useState(true);
  const basketId = useBasketId();

  /*  TODO (mert.ohancan) ignores will be removed when the whole modals are implemented */
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  const Modal = modals[loyaltyType];
  const bid = basketId ? { basketId: basketId } : {};

  const [isValidate, setIsValidate] = useState(false);

  const commonActions = async (basketId: string) => {
    if (typeof window !== 'undefined')
      localStorage.setItem('basketId', basketId);
    action?.();
    setIsValidate(true);
  };

  const toggleErrorModal = () => setError((val) => !val);

  const handleTurkTelekomValidate = async (form: TurkTelekomModalFormData) => {
    const { phoneNumber, turkTelekomCode } = form;
    if (!phoneNumber || !turkTelekomCode) return handleError();

    if (phoneNumber && turkTelekomCode) {
      try {
        const { basketId: id } = await LoyaltiesService[
          getTurkTelekomService(loyaltyType)
        ]({
          number: phoneNumber.trim(),
          code: turkTelekomCode.trim(),
          ...bid,
        });
        if (id) {
          commonActions(id);
          storeLoyalty(loyaltyType, true);
        }
      } catch (error) {
        /*  TODO (mert.ohancan) should be handled with error boundary */
        toggleErrorModal();
      }
    }
  };

  const handleHopiValidate = async (form: HopiModalFormData) => {
    const { hopiCode } = form;
    if (!hopiCode) return handleError();

    try {
      const { basketId: id } = await callService(
        'LoyaltiesService',
        'validateHopi',
        {
          item: hopiCode,
          type: 'TOKEN',
          ...bid,
        }
      );
      if (id) {
        commonActions(id);
      }
    } catch (error) {
      /*  TODO (mert.ohancan) should be handled with error boundary */
      toggleErrorModal();
    }
  };

  const handleKocAilemValidate = async (form: KocAilemModalFormData) => {
    const { number, type } = form;
    if (!number) return handleError();

    try {
      const { basketId: id } = await callService(
        'LoyaltiesService',
        'validateKocAilem',
        {
          type: type === 0 ? 'TCKN' : 'KARTNO',
          number,
          ...bid,
        }
      );

      if (id) {
        commonActions(id);
        if (typeof window !== 'undefined')
          localStorage.setItem('hasKocAilemLoyalty', 'true');
        storeLoyalty(loyaltyType, true);
      }
    } catch (error) {
      /*  TODO (mert.ohancan) should be handled with error boundary */
      toggleErrorModal();
    }
  };

  const handleZubizuValidate = async (form: ZubizuModalFormData) => {
    const { zubizuCode } = form;

    if (!zubizuCode) return handleError();

    try {
      const { basketId: id } = await callService(
        'LoyaltiesService',
        'validateZubizu',
        {
          code: zubizuCode,
          ...bid,
        }
      );

      if (id) {
        commonActions(id);
      }
    } catch (error) {
      /*  TODO (mert.ohancan) should be handled with error boundary */
      toggleErrorModal();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = (formData: any): Promise<void> | null => {
    switch (loyaltyType) {
      case LoyaltyType.TURK_TELEKOM:
      case LoyaltyType.TURK_TELEKOM_PRIME:
      case LoyaltyType.TURK_TELEKOM_PRIME_BUSINESS:
        return handleTurkTelekomValidate(formData);
      case LoyaltyType.HOPI_KAZANIM:
        return handleHopiValidate(formData);
      case LoyaltyType.KOC_AILEM:
        return handleKocAilemValidate(formData);
      case LoyaltyType.ZUBIZU:
        return handleZubizuValidate(formData);
      default:
        return null;
    }
  };

  const handleCancel = (): void => {
    setShowModal(false);
    onCancel && onCancel();
  };

  const promotionType = useMemo(() => {
    const { TURK_TELEKOM_PRIME, TURK_TELEKOM_PRIME_BUSINESS } = LoyaltyType;

    const types = {
      [TURK_TELEKOM_PRIME]: 'prime',
      [TURK_TELEKOM_PRIME_BUSINESS]: 'business',
    };

    if (
      loyaltyType === TURK_TELEKOM_PRIME ||
      loyaltyType === TURK_TELEKOM_PRIME_BUSINESS
    )
      return types[loyaltyType];
  }, [loyaltyType]);

  useEffect(() => {
    if (error) {
      setShowModal(false);
    }
  }, [error]);

  const handleError = () => {
    setShowModal(false);
    toggleErrorModal();
  };

  const handleModalErrorCancel = () => {
    setShowModal(true);
    toggleErrorModal();
  };

  if (error)
    return (
      <ErrorModal
        onCancel={handleModalErrorCancel}
        isVisible={true}
        message='Girdiğiniz indirim kodu hatalıdır.'
      />
    );

  return (
    <Modal
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onSubmit={(formData: any) => handleSubmit(formData)}
      onCancel={handleCancel}
      isVisible={showModal}
      promotion={promotionType}
      isValidated={isValidate}
    />
  );
};

export default LoyaltyVerifications;
